<template>
  <div class="p-competing-suggest-keywords">
    <div class="p-tasklist-form--checked">
      <p class="p-tasklist-form--checked__count">
        選択数 : <span class="c-text--bold">{{ params.checked.length }}</span>
      </p>
      <Button variant="border" fsize="f12" size="w120" @click="reset">
        <Icon variant="return_gn" :size="12" />
        選択を解除する
      </Button>
    </div>
    <template v-if="!isLoading">
      <template v-if="version == 1">
        <div class="c-text--alert">
          <Icon2 variant="attention" :size="12" />
          「記事対策キーワード機能」および「重複チェック機能」をご利用いただくには、お手数ですが再分析をお願いします。
        </div>
      </template>
      <template v-else-if="analysesStatus != 2">
        <div class="c-text--alert">
          <Icon2 variant="attention" :size="12" />
          分析中のため「記事対策キーワード機能」および「重複チェック機能」はご利用できません。しばらくお待ちください。
        </div>
      </template>
    </template>
    <Tab>
      <TabItem
        v-for="tab in tabs"
        :key="tab.key"
        :id="tab.key"
        :active="selectedTab"
        :disabled="tab.disabled"
        @click="updateTab"
      >
        {{ tab.label }}
        <Tooltip v-if="tab.key == 'priority'" size="sm">
          SEARCH
          WRITEで検索結果画面のタイトルから該当ページに訪れるユーザーの意図を読み取り自動で分類します。<br />
          「知りたい」が検索結果の上位10件中6件以上を占めるキーワードは、
          新規コンテンツがユーザーのニーズに合致しやすく上位を目指せる可能性が高いため、優先的にコンテンツを作成しましょう。
        </Tooltip>
      </TabItem>
    </Tab>
    <div class="c-table">
      <div class="c-virtual-list__header">
        <thead class="c-virtual-list__head">
          <th width="29"></th>
          <ThSort
            v-model="params.sort"
            name="keyword"
            orderby="asc"
            type="string"
          >
            キーワード
          </ThSort>
          <ThSort
            v-model="params.sort"
            :disabled="!duplicateCheckParams.keyword"
            name="duplicatePagesCount"
            orderby="desc"
            type="number"
            width="105"
          >
            <span>重複ページ</span>
            <Tooltip size="sm">
              キーワード横の「重複チェック」ボタンをクリックすると、検索結果を表示中のキーワードと他のキーワードの検索結果を照合し、上位10件のうち重複しているページ数を表示します。
              ※重複ページが10件のキーワードは、同一キーワード（表記ゆれなど）の可能性があります。
            </Tooltip>
          </ThSort>
          <ThSort
            v-model="params.sort"
            name="searchVolume"
            orderby="desc"
            type="number"
            width="100"
          >
            月間検索数
          </ThSort>
          <ThSort
            v-model="params.sort"
            name="rank"
            orderby="asc"
            type="number"
            width="120"
          >
            <div class="flex">
              <Label size="sm">自社</Label>
              <a
                :href="project.siteUrl"
                target="_blank"
                class="c-text--ellipsisTip"
                @click.stop
              >
                <EllipsisTip>{{ project.siteUrl }}</EllipsisTip>
              </a>
            </div>
            <p class="rank">想定流入数・順位</p>
          </ThSort>
        </thead>
      </div>
      <div class="c-virtual-list__content" ref="content">
        <RecycleScroller
          :items="sortedItems"
          :item-size="scroller.itemSize"
          key-field="keyword"
          :buffer="1000"
          page-mode
        >
          <template slot="before">
            <div v-if="isLoading" class="is-status">
              <Loading variant="inline" />
            </div>
            <div v-else-if="!items.length" class="is-status">
              キーワードが取得できませんでした。
            </div>
            <div v-else-if="!sortedItems.length" class="is-status">
              <template
                v-if="selectedTab === 'priority' && !priorityItems.length"
              >
                記事対策キーワードに該当するキーワードが分析結果にありませんでした。
              </template>
              <template v-else>
                条件に合うキーワードがありません。
              </template>
            </div>
          </template>
          <template v-slot="{ item }">
            <ListItemSuggestAnalysis
              :key="item.keyword"
              :checked="params.checked"
              :item="item"
              :maxSearchVolume="maxSearchVolume"
              :selected="selected"
              :selectedTab="selectedTab"
              :tasks="tasks"
              :completedTasks="completedTasks"
              :duplicateCheckParams="duplicateCheckParams"
              :isDuplicateCheckEnabled="isDuplicateCheckEnabled"
              @change="checkItem"
              @select="selectItem"
              @duplicateCheck="duplicateCheck"
            />
          </template>
        </RecycleScroller>
      </div>
    </div>
    <portal to="form-add-tasks">
      <transition name="v-fade">
        <FormAddTasks
          v-if="params.checked.length"
          :count="params.checked.length"
          :date="startDate"
          :items="params.checked"
          :project="project"
          defaultType="createContent"
          page="competitiveKeyword"
          key="form"
          :version="version"
          @pushSubmitEvent="pushSubmitEvent"
        />
      </transition>
    </portal>
    <div class="p-tasklist-form--checked">
      <p class="p-tasklist-form--checked__count">
        選択数 : <span class="c-text--bold">{{ params.checked.length }}</span>
      </p>
      <Button variant="border" fsize="f12" size="w120" @click="reset">
        <Icon variant="return_gn" :size="12" />
        選択を解除する
      </Button>
    </div>
  </div>
</template>

<script>
import Button from '@/components/Button';
import Icon from '@/components/Icon';
import Icon2 from '@/components/Icon2';
import EllipsisTip from '@/components/EllipsisTip';
import FormAddTasks from '@/components/FormAddTasks';
import Label from '@/components/Label';
import ListItemSuggestAnalysis from '@/components/competitiveKeyword/ListItemSuggestAnalysis';
import Loading from '@/components/Loading';
import Tab from '@/components/Tab';
import TabItem from '@/components/TabItem';
import ThSort from '@/components/ThSort';
import Tooltip from '@/components/Tooltip';
import { RecycleScroller } from 'vue-virtual-scroller';
import utils from '@/utils';
import Amplitude from '@/amplitude';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
  props: {
    items: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    selected: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    version: {
      type: Number,
      required: true
    },
    analysesStatus: {
      type: Number,
      required: true
    },
    tasks: {
      type: Array,
      required: true
    },
    completedTasks: {
      type: Array,
      required: true
    },
    keywordId: {
      type: String,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    duplicateCheckParams: {
      type: Object,
      required: false
    }
  },
  components: {
    Button,
    Icon,
    Icon2,
    EllipsisTip,
    FormAddTasks,
    Label,
    ListItemSuggestAnalysis,
    Loading,
    Tab,
    TabItem,
    ThSort,
    Tooltip,
    RecycleScroller
  },
  data() {
    return {
      tabs: [
        {
          key: 'priority',
          label: '記事対策キーワード',
          disabled: false
        },
        {
          key: 'all',
          label: 'すべてのキーワード',
          disabled: false
        }
      ],
      selectedTab: 'priority',
      isDuplicateCheckEnabled: false,
      params: {
        checked: [],
        sort: {
          name: 'searchVolume',
          orderby: 'desc',
          type: 'number'
        }
      },
      scroller: {
        itemSize: 66
      }
    };
  },
  computed: {
    priorityItems() {
      if (this.version == 1 || this.analysesStatus != 2) {
        return this.items;
      }

      return this.items.filter(item => {
        return item.isPriority;
      });
    },
    maxSearchVolume() {
      let max = 0;
      for (let i = 0; i < this.items.length; i++) {
        let keyword = this.items[i];
        if (keyword.searchVolumeByProject > max) {
          max = keyword.searchVolumeByProject;
        }
      }
      return max;
    },
    notificationsHeight() {
      return this.$store.state.view.size.notificationsHeight;
    },
    sortedItems() {
      let items =
        this.selectedTab == 'priority'
          ? this.priorityItems.slice()
          : this.items.slice();

      // 重複チェック対象のキーワードを無理やり最上位にするための処理
      if (this.params.sort.name == 'duplicatePagesCount') {
        const index = items.findIndex(
          item => item.keyword === this.duplicateCheckParams.keyword
        );

        if (index != -1) {
          items[index].duplicatePagesCount =
            this.params.sort.orderby == 'desc' ? 100 : -100;
        }
      }

      return utils.sort(
        items,
        this.params.sort.type,
        this.params.sort.orderby,
        this.params.sort.name
      );
    }
  },
  watch: {
    version: {
      handler() {
        this.checkVersionAndStatus();
      },
      immediate: true
    },
    analysesStatus: {
      handler() {
        this.checkVersionAndStatus();
      },
      immediate: true
    },
    selected: {
      // 選択キーワードが変わったタイミングで、重複チェックのエラーをリセット
      handler() {
        this.duplicateCheckParams.hasError = false;
      },
      immediate: true
    }
  },
  methods: {
    updateTab(value) {
      this.selectedTab = value;
      this.$emit('updateTab', value);
    },
    checkItem(checked) {
      this.params.checked = checked;
    },
    duplicateCheck(item) {
      this.$emit('duplicateCheck', item);
    },
    uncheckItem(item) {
      const checkedIndex = this.params.checked.indexOf(item);
      if (checkedIndex !== -1) {
        this.params.checked.splice(checkedIndex, 1);
      }
    },
    selectItem(item) {
      this.$emit('select', item);
    },
    reset() {
      this.params.checked = [];
    },
    checkVersionAndStatus() {
      // バージョンが2かつ分析完了ステータスのもののみ記事対策キーワードを表示する。
      // デフォルトのPropsの値に影響されないため、念のため明示的に記事対策キーワードにタブを変更する分岐も入れておく
      if (this.version == 2 && this.analysesStatus == 2) {
        this.tabs[0].disabled = false;
        this.selectedTab = 'priority';
        this.$emit('updateTab', 'priority');

        // バージョンが2かつ分析完了ステータスのもののみ重複チェックが可能。
        this.isDuplicateCheckEnabled = true;
      } else {
        this.tabs[0].disabled = true;
        this.selectedTab = 'all';
        this.$emit('updateTab', 'all');
      }
    },
    pushSubmitEvent() {
      this.params.checked.forEach(item => {
        const properties = {
          keyword: item.keyword,
          searchVolume: item.searchVolume
        };
        Amplitude.postLogEvent(
          'submitTaskWithSearchVolume - relatedWordAnalysis',
          properties
        );
      });
    },
    scrollToKeyword(keyword) {
      const index = this.sortedItems.findIndex(
        item => item.keyword === keyword
      );
      if (index === -1) {
        return;
      }
      // 位置が微妙に1pxずれるので+1している
      const scrollTop =
        index * this.scroller.itemSize + 1 - this.notificationsHeight;

      // 該当のキーワードまでスクロールする
      this.$scrollTo(this.$refs.content, 500, {
        offset: scrollTop,
        onDone: () => {
          // 移動してからキーワードを選択状態に変更
          this.selectItem(this.sortedItems[index]);
        }
      });
    }
  }
};
</script>
